/**
 * Phase 36a — Axis Bank CIT portal (Wave 1).
 * Driver roster + KYC flow for BANK_P2 tenants.
 */
import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  UserRound, Plus, Search, Loader2, Edit2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';

const BLANK = {
  name: '', cit_vendor_id: '', phone: '', email: '', date_of_birth: '',
  aadhaar: '', license_number: '', license_class: 'LMV-TR', license_expiry: '',
};
const BLANK_FILES = { aadhaar_front: null, aadhaar_back: null, license_doc: null };

export default function OpsDriversPage() {
  const { tenant }                 = useTrackOticAuth();
  const [loading, setLoading]      = useState(true);
  const [saving,  setSaving]       = useState(false);
  const [drivers, setDrivers]      = useState([]);
  const [vendors, setVendors]      = useState([]);
  const [searchQuery, setSearchQ]  = useState('');
  const [vendorFilter, setVendor]  = useState('');
  const [kycFilter, setKyc]        = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]      = useState(null);
  const [form, setForm]            = useState(BLANK);
  const [files, setFiles]          = useState(BLANK_FILES);
  const [existingDocs, setExistingDocs] = useState({ aadhaar_front_url: '', aadhaar_back_url: '', license_url: '' });
  const [rejectDialog, setReject]  = useState({ open: false, driver: null, reason: '' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (vendorFilter) params.vendor_id  = vendorFilter;
      if (kycFilter)    params.kyc_status = kycFilter;
      const [drvRes, vendorsRes] = await Promise.all([
        opsApi.getCitDrivers(params),
        opsApi.getCitVendors({}),
      ]);
      setDrivers(drvRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load drivers');
      setDrivers([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [vendorFilter, kycFilter]);

  const openCreate = () => {
    setEditing(null); setForm(BLANK); setFiles(BLANK_FILES);
    setExistingDocs({ aadhaar_front_url: '', aadhaar_back_url: '', license_url: '' });
    setDialogOpen(true);
  };
  const openEdit = async (d) => {
    setEditing(d);
    setForm({
      name:            d.name || '',
      cit_vendor_id:   d.cit_vendor_id || '',
      phone:           d.phone || '',
      email:           d.email || '',
      date_of_birth:   d.date_of_birth || '',
      aadhaar:         '',  // never pre-fill
      license_number:  d.license_number || '',
      license_class:   d.license_class || 'LMV-TR',
      license_expiry:  d.license_expiry || '',
    });
    setFiles(BLANK_FILES);
    setExistingDocs({
      aadhaar_front_url: d.aadhaar_front_url || '',
      aadhaar_back_url:  d.aadhaar_back_url  || '',
      license_url:       d.license_url       || '',
    });
    setDialogOpen(true);
    try {
      const full = await opsApi.getCitDriver(d.id);
      setExistingDocs({
        aadhaar_front_url: full.data?.aadhaar_front_url || '',
        aadhaar_back_url:  full.data?.aadhaar_back_url  || '',
        license_url:       full.data?.license_url       || '',
      });
    } catch { /* fall back to row-level view */ }
  };

  const buildPayload = (fileFields) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (!v) return;  // skip empties — nullable fields treat absence as null
      fd.append(k, v);
    });
    Object.entries(fileFields).forEach(([k, f]) => { if (f) fd.append(k, f); });
    return fd;
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.license_number.trim() || !form.license_expiry) {
      toast.error('Name, phone, license number and expiry are required'); return;
    }
    if (!editing && (!files.aadhaar_front || !files.aadhaar_back || !files.license_doc)) {
      toast.error('Upload Aadhaar front, Aadhaar back and driving licence'); return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { aadhaar: _ignored, ...rest } = form;
        const hasFiles = files.aadhaar_front || files.aadhaar_back || files.license_doc;
        if (hasFiles) {
          const fd = new FormData();
          Object.entries(rest).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') fd.append(k, v);
          });
          Object.entries(files).forEach(([k, f]) => { if (f) fd.append(k, f); });
          await opsApi.updateCitDriver(editing.id, fd);
        } else {
          await opsApi.updateCitDriver(editing.id, { ...rest, cit_vendor_id: form.cit_vendor_id || null });
        }
        toast.success('Driver updated');
      } else {
        await opsApi.createCitDriver(buildPayload(files));
        toast.success('Driver submitted for KYC');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      const errs = err.response?.data?.errors;
      const firstErr = errs ? Object.values(errs)[0]?.[0] : null;
      toast.error(firstErr || err.response?.data?.detail || err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleApproveKyc = async (d) => {
    if (!window.confirm(`Approve KYC for ${d.name}?`)) return;
    try {
      await opsApi.approveCitDriverKyc(d.id);
      toast.success('KYC approved'); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
  };

  const handleRejectKyc = async () => {
    if (!rejectDialog.reason || rejectDialog.reason.length < 5) {
      toast.error('Rejection reason must be at least 5 characters'); return;
    }
    try {
      await opsApi.rejectCitDriverKyc(rejectDialog.driver.id, { reason: rejectDialog.reason });
      toast.success('KYC rejected');
      setReject({ open: false, driver: null, reason: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reject failed'); }
  };

  const filtered = drivers.filter(
    (d) => d.name?.toLowerCase().includes(searchQuery.toLowerCase())
        || d.license_number?.toLowerCase().includes(searchQuery.toLowerCase())
        || d.phone?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const verifiedCount = drivers.filter((d) => d.kyc_status === 'verified').length;
  const pendingCount  = drivers.filter((d) => d.kyc_status === 'pending').length;
  const expSoonCount  = drivers.filter((d) => d.license_days_to_expiry !== null && d.license_days_to_expiry < 60 && d.license_days_to_expiry >= 0).length;

  return (
    <div className="p-8" data-testid="ops-drivers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <UserRound className="w-8 h-8 text-teal-500" />
            Drivers
          </h1>
          <p className="text-slate-400 mt-1">CIT drivers for {tenant?.name}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchAll} variant="outline" className="border-slate-700 text-slate-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="ops-add-driver-btn">
            <Plus className="w-4 h-4 mr-2" /> Add driver
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={UserRound} label="Total" value={drivers.length} />
        <StatCard icon={CheckCircle2} label="KYC verified" value={verifiedCount} valueClass="text-green-400" />
        <StatCard icon={AlertTriangle} label="KYC pending" value={pendingCount} valueClass="text-amber-400" />
        <StatCard icon={AlertTriangle} label="License expiring (<60d)" value={expSoonCount} valueClass="text-red-400" />
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
          <Input
            placeholder="Search by name, phone or license..."
            value={searchQuery}
            onChange={(e) => setSearchQ(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-white"
          />
        </div>
        <select value={vendorFilter} onChange={(e) => setVendor(e.target.value)}
                className="rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2">
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={kycFilter} onChange={(e) => setKyc(e.target.value)}
                className="rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2">
          <option value="">All KYC states</option>
          <option value="pending">Pending</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Driver</TableHead>
              <TableHead className="text-slate-400">Vendor</TableHead>
              <TableHead className="text-slate-400">Mobile</TableHead>
              <TableHead className="text-slate-400">Aadhaar</TableHead>
              <TableHead className="text-slate-400">License</TableHead>
              <TableHead className="text-slate-400">Valid till</TableHead>
              <TableHead className="text-slate-400">KYC</TableHead>
              <TableHead className="text-slate-400">Account</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-500">
                No drivers found
              </TableCell></TableRow>
            ) : (
              filtered.map((d) => (
                <TableRow key={d.id} className="border-slate-800" data-testid={`ops-driver-row-${d.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-white font-medium">{d.name}</p>
                      <p className="text-slate-500 text-xs">{d.license_class || '—'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300">{d.cit_vendor_name || '—'}</TableCell>
                  <TableCell className="text-slate-300 font-mono">{d.phone || '—'}</TableCell>
                  <TableCell className="text-slate-400 font-mono text-sm">{d.aadhaar_masked || '—'}</TableCell>
                  <TableCell className="text-slate-300 font-mono text-sm">{d.license_number}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300 text-sm">{d.license_expiry || '—'}</span>
                      {d.license_days_to_expiry !== null && d.license_days_to_expiry >= 0 && d.license_days_to_expiry < 60 && (
                        <AlertTriangle className="w-4 h-4 text-amber-400" title={`Expires in ${d.license_days_to_expiry}d`} />
                      )}
                      {d.license_days_to_expiry !== null && d.license_days_to_expiry < 0 && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Expired</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell><Badge className={kycBadge(d.kyc_status)}>{d.kyc_status}</Badge></TableCell>
                  <TableCell><Badge className={d.account_status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}>{d.account_status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {d.kyc_status === 'pending' && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => handleApproveKyc(d)}
                                  className="h-8 w-8 text-slate-400 hover:text-green-400" title="Approve KYC"
                                  data-testid={`approve-kyc-${d.id}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon"
                                  onClick={() => setReject({ open: true, driver: d, reason: '' })}
                                  className="h-8 w-8 text-slate-400 hover:text-red-400" title="Reject KYC"
                                  data-testid={`reject-kyc-${d.id}`}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openEdit(d)}
                              className="h-8 w-8 text-slate-400 hover:text-teal-400" data-testid={`edit-driver-${d.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? 'Edit driver' : 'Add driver'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editing ? 'Update driver details.' : 'New drivers are created with KYC status "pending".'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <FormField label="Full name (as on Aadhaar)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} span2 />
            <div className="col-span-2">
              <Label className="text-slate-300">CIT vendor</Label>
              <select
                value={form.cit_vendor_id}
                onChange={(e) => setForm({ ...form, cit_vendor_id: e.target.value })}
                className="w-full mt-1 rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
              >
                <option value="">— None —</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <FormField label="Mobile (OTP login)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <FormField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <FormField label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} />
            {!editing && (
              <FormField label="Aadhaar (12 digits)" value={form.aadhaar} onChange={(v) => setForm({ ...form, aadhaar: v })} placeholder="123412341234" />
            )}
            <FormField label="Driving licence number" value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} />
            <div>
              <Label className="text-slate-300">Licence class</Label>
              <select
                value={form.license_class}
                onChange={(e) => setForm({ ...form, license_class: e.target.value })}
                className="w-full mt-1 rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
              >
                <option value="LMV">LMV</option>
                <option value="LMV-TR">LMV-TR</option>
                <option value="HMV">HMV</option>
              </select>
            </div>
            <FormField label="Licence valid till" type="date" value={form.license_expiry} onChange={(v) => setForm({ ...form, license_expiry: v })} />

            <FileField
              label={`Aadhaar front${editing ? ' (replace)' : ' *'}`}
              file={files.aadhaar_front}
              hasExisting={!!existingDocs.aadhaar_front_url}
              onChange={(f) => setFiles({ ...files, aadhaar_front: f })}
            />
            <FileField
              label={`Aadhaar back${editing ? ' (replace)' : ' *'}`}
              file={files.aadhaar_back}
              hasExisting={!!existingDocs.aadhaar_back_url}
              onChange={(f) => setFiles({ ...files, aadhaar_back: f })}
            />
            <FileField
              label={`Driving licence${editing ? ' (replace)' : ' *'}`}
              file={files.license_doc}
              hasExisting={!!existingDocs.license_url}
              onChange={(f) => setFiles({ ...files, license_doc: f })}
              span2
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSave} disabled={saving} data-testid="save-driver-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save changes' : 'Submit for KYC')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* KYC reject dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setReject({ ...rejectDialog, open })}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Reject KYC — {rejectDialog.driver?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Provide a reason for rejection (saved to the audit log).
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={rejectDialog.reason}
            onChange={(e) => setReject({ ...rejectDialog, reason: e.target.value })}
            placeholder="Reason..."
            rows={4}
            className="w-full rounded-md border border-slate-800 bg-slate-900 text-white px-3 py-2"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-slate-700 text-slate-300"
                    onClick={() => setReject({ open: false, driver: null, reason: '' })}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleRejectKyc} data-testid="confirm-reject-kyc">
              Reject KYC
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function kycBadge(s) {
  switch (s) {
    case 'verified': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'pending':  return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/30';
    default:         return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}

function StatCard({ icon: Icon, label, value, valueClass = 'text-white' }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
        <Icon className="w-5 h-5 text-teal-400" />
      </div>
      <div>
        <p className="text-slate-500 text-xs uppercase">{label}</p>
        <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', span2 = false, placeholder = '' }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <Label className="text-slate-300">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 bg-slate-900 border-slate-800 text-white"
      />
    </div>
  );
}

function FileField({ label, file, hasExisting, onChange, span2 = false }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <Label className="text-slate-300">{label}</Label>
      <Input
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="mt-1 bg-slate-900 border-slate-800 text-white file:text-slate-300 file:bg-slate-800 file:border-0 file:mr-3 file:px-2 file:py-1 file:rounded"
      />
      <p className="text-xs text-slate-500 mt-1">
        {file ? `Selected: ${file.name}` : hasExisting ? 'A document is already on file — upload to replace.' : 'JPG/PNG/PDF, max 5 MB.'}
      </p>
    </div>
  );
}
